import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import ConfirmModal from '../Elements/Modal/ConfirmModal';

// Contexto para el flujo de "¿Quieres guardar los cambios antes de salir?".
// Lo consumen los formularios de Recurso (add-recurso.js / edit-recurso.js) para
// registrar si hay cambios sin guardar y cómo guardarlos, y lo consumen Header/SessionUser
// para interceptar la navegación (logo / cerrar sesión) cuando corresponda.
const UnsavedChangesContext = createContext(null);

export const UnsavedChangesProvider = ({ children }) => {
    // hasUnsavedChanges: si el formulario activo (si hay uno) reporta cambios sin guardar.
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    // saveHandler: función que el formulario activo registra, con firma (onSuccess) => void.
    // onSuccess se invoca solo si el guardado terminó con éxito.
    const saveHandlerRef = useRef(null);

    const [showExitModal, setShowExitModal] = useState(false);
    const pendingActionRef = useRef(null);

    const registerFormGuard = useCallback((hasChanges, saveHandler) => {
        setHasUnsavedChanges(!!hasChanges);
        saveHandlerRef.current = saveHandler || null;
    }, []);

    const clearFormGuard = useCallback(() => {
        setHasUnsavedChanges(false);
        saveHandlerRef.current = null;
    }, []);

    // Header/SessionUser llaman a esto en vez de navegar directo.
    // Si no hay cambios sin guardar (o no estamos en un formulario protegido), ejecuta la acción de una vez.
    const requestNavigation = useCallback((action) => {
        if (hasUnsavedChanges && typeof saveHandlerRef.current === 'function') {
            pendingActionRef.current = action;
            setShowExitModal(true);
        } else {
            action();
        }
    }, [hasUnsavedChanges]);

    const handleSaveAndExit = useCallback(() => {
        const action = pendingActionRef.current;
        if (typeof saveHandlerRef.current === 'function') {
            // El guardado real (callFunctionButton) decide si navega o no según éxito/fallo.
            // Solo en éxito se continúa con la acción original pendiente (logout/home).
            saveHandlerRef.current(() => {
                setShowExitModal(false);
                pendingActionRef.current = null;
                if (action) action();
            });
        }
    }, []);

    const handleExitWithoutSaving = useCallback(() => {
        const action = pendingActionRef.current;
        setShowExitModal(false);
        pendingActionRef.current = null;
        if (action) action();
    }, []);

    const handleCancelExit = useCallback(() => {
        setShowExitModal(false);
        pendingActionRef.current = null;
    }, []);

    return (
        <UnsavedChangesContext.Provider value={{
            registerFormGuard,
            clearFormGuard,
            requestNavigation,
        }}>
            {children}
            <ConfirmModal
                id="exit-unsaved-changes-modal"
                show={showExitModal}
                onHidden={() => setShowExitModal(false)}
                title="¿Quiere guardar los cambios antes de salir?"
                titleBold={false}
                body="Perderá la información que no haya guardado."
                cancelLabel="Salir sin guardar"
                cancelVariant="negativo"
                onCancel={handleExitWithoutSaving}
                confirmLabel="Guardar y salir"
                confirmVariant="principal"
                onConfirm={handleSaveAndExit}
            />
        </UnsavedChangesContext.Provider>
    );
};

export const useUnsavedChanges = () => {
    const ctx = useContext(UnsavedChangesContext);
    if (!ctx) {
        // Fuera del Provider (no debería pasar en este árbol), devolvemos un fallback inocuo
        // para que Header/SessionUser no rompan si algún día se renderizan aislados (ej. tests).
        return {
            registerFormGuard: () => {},
            clearFormGuard: () => {},
            requestNavigation: (action) => action(),
        };
    }
    return ctx;
};
